import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Search, Users } from 'lucide-react';
import type { TFunction } from 'i18next';
import { useDeferredValue, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { DataTable, type Column } from '@/components/ui/data-table';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { PageHeader } from '@/components/page-header';
import { NewCustomerDialog } from './new-customer-dialog';
import { request } from '@/lib/api';
import { customerListSchema, customerSchema, type Customer } from '@/lib/schemas';
import { formatRelative } from '@/lib/utils';
import { useCanEdit } from '@/lib/auth';
import { LearnFromDocumentsDialog } from '@/features/learning/learn-dialog';

const PAGE_SIZE = 50;

/** SearchCustomersQueryDto מחייב שני תווים — מתחת לזה השרת מחזיר 400. */
const MIN_SEARCH_LENGTH = 2;

/** `/customers/search` מחזיר מערך שטוח, לא עמוד מעומד כמו `/customers`. */
const customerSearchSchema = z.array(customerSchema);

const buildColumns = (t: TFunction<['customers', 'common']>): Array<Column<Customer>> => [
  {
    key: 'name',
    header: t('columns.name'),
    cell: (c) => (
      <Link
        to="/customers/$customerId"
        params={{ customerId: c.id }}
        className="font-medium text-fg hover:text-accent"
      >
        {c.name}
      </Link>
    ),
  },
  {
    key: 'email',
    header: t('columns.email'),
    cell: (c) =>
      c.email ? (
        // ltr-inline — בלי זה ה-@ והנקודות קופצים לצד הלא נכון בשורה עברית.
        <a href={`mailto:${c.email}`} className="ltr-inline text-fg-muted hover:text-accent">
          {c.email}
        </a>
      ) : (
        <span className="text-fg-subtle">—</span>
      ),
  },
  {
    key: 'phone',
    header: t('columns.phone'),
    cell: (c) =>
      c.phone ? (
        <a href={`tel:${c.phone}`} className="ltr-inline tabular text-fg-muted hover:text-accent">
          {c.phone}
        </a>
      ) : (
        <span className="text-fg-subtle">—</span>
      ),
  },
  {
    key: 'createdAt',
    header: t('columns.createdAt'),
    cell: (c) => (
      <time dateTime={c.createdAt} className="text-xs text-fg-subtle">
        {formatRelative(c.createdAt)}
      </time>
    ),
  },
];

export function CustomersPage() {
  const canEdit = useCanEdit();
  const { t } = useTranslation(['customers', 'common']);
  const columns = useMemo(() => buildColumns(t), [t]);
  const [query, setQuery] = useState('');
  // useDeferredValue — ההקלדה לא נחסמת ו-React נוטש רינדור מיושן,
  // במקום להמתין זמן קבוע כמו ב-debounce ידני.
  const deferredQuery = useDeferredValue(query);
  const term = deferredQuery.trim();
  const isSearching = term.length >= MIN_SEARCH_LENGTH;

  const list = useInfiniteQuery({
    queryKey: ['customers', 'list'],
    queryFn: ({ pageParam, signal }) =>
      request(`/customers?take=${PAGE_SIZE}${pageParam ? `&cursor=${pageParam}` : ''}`, {
        schema: customerListSchema,
        signal,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: !isSearching,
  });

  const search = useQuery({
    queryKey: ['customers', 'search', term],
    queryFn: ({ signal }) =>
      request(`/customers/search?q=${encodeURIComponent(term)}`, {
        schema: customerSearchSchema,
        signal,
      }),
    enabled: isSearching,
  });

  const active = isSearching ? search : list;
  const rows: Customer[] = isSearching
    ? (search.data ?? [])
    : (list.data?.pages.flatMap((p) => p.items) ?? []);

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <PageHeader
        title={t('page.title')}
        description={t('page.description')}
        action={
          canEdit ? (
            <div className="flex flex-wrap gap-2">
              <LearnFromDocumentsDialog />
              <NewCustomerDialog />
            </div>
          ) : undefined
        }
      />

      <div className="relative max-w-sm">
        {/* start-3 — ב-RTL האייקון יושב בימין, בלי CSS נפרד לכל כיוון. */}
        <Search
          className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-fg-subtle"
          aria-hidden
        />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('page.searchPlaceholder')}
          aria-label={t('page.searchAria')}
          className="ps-9"
        />
        {query.trim().length > 0 && !isSearching ? (
          <p className="mt-1.5 text-2xs text-fg-subtle">{t('page.minChars')}</p>
        ) : null}
      </div>

      <Card className="overflow-hidden">
        {active.isLoading ? (
          <div className="divide-y divide-border" aria-busy>
            {Array.from({ length: 8 }, (_, i) => (
              <div key={i} className="flex items-center gap-4 px-4 py-4">
                <Skeleton className="h-4 flex-1" />
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-16" />
              </div>
            ))}
          </div>
        ) : active.isError ? (
          <ErrorState error={active.error} onRetry={() => void active.refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={Users}
            title={isSearching ? t('page.notFoundTitle') : t('page.emptyTitle')}
            description={
              isSearching ? t('page.notFoundDescription', { term }) : t('page.emptyDescription')
            }
            action={
              canEdit ? (
                isSearching ? (
                  <NewCustomerDialog />
                ) : (
                  <div className="flex flex-wrap justify-center gap-2">
                    <LearnFromDocumentsDialog />
                    <NewCustomerDialog />
                  </div>
                )
              ) : undefined
            }
          />
        ) : (
          <>
            <DataTable
              caption={t('page.caption')}
              columns={columns}
              rows={rows}
              rowKey={(c) => c.id}
            />
            {!isSearching && list.hasNextPage ? (
              <div className="flex justify-center border-t border-border p-3">
                <Button
                  variant="secondary"
                  onClick={() => void list.fetchNextPage()}
                  loading={list.isFetchingNextPage}
                >
                  {t('page.loadMore')}
                </Button>
              </div>
            ) : null}
            {isSearching ? (
              <p className="border-t border-border px-4 py-2.5 text-2xs text-fg-subtle">
                {t('page.searchLimit')}
              </p>
            ) : null}
          </>
        )}
      </Card>
    </div>
  );
}
